class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindDrawerChrome();
    this.setHeaderCartIconAccessibility();
  }

  bindDrawerChrome() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundClose);
  }

  syncEmptyState(isEmpty) {
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    if (!isEmpty) {
      this.querySelector('.drawer__inner-empty')?.remove();
    }
  }

  replaceFromSectionHtml(sectionHtml) {
    if (!sectionHtml) return false;
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const source = parsed.querySelector('#CartDrawer');
    const target = this.querySelector('#CartDrawer') || document.getElementById('CartDrawer');
    if (!source || !target) return false;
    target.innerHTML = source.innerHTML;
    this.syncEmptyState(!target.querySelector('.cart-item'));
    this.bindDrawerChrome();
    return true;
  }

  updateContents(parsedState) {
    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    const replaced = this.replaceFromSectionHtml(sectionHtml);
    const bubbleHtml = parsedState?.sections?.['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = this.getSectionInnerHTML(bubbleHtml);
      if (bubble && html) bubble.innerHTML = html;
    }
    if (!replaced) return false;
    this.syncEmptyState(false);
    this.skipNextItemsUpdate = true;
    return true;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const hasItems = Boolean(this.querySelector('.cart-item'));
    this.syncEmptyState(!hasItems);
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.updateContents(parsedState);
    setTimeout(() => {
      this.bindDrawerChrome();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML || parsed.querySelector('.shopify-section')?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  static swipeMediaQuery = '(max-width: 749px)';
  static swipeReveal = 92;
  static swipeIgnore = 'quantity-input, cart-remove-button, .quantity-popover__info-button, .quantity-popover__info, summary, textarea, .cart-item__swipe-action';

  constructor() {
    super();
    this.swipeState = null;
    this.swipeSuppressClick = false;
    this.onSwipePointerDown = this.onSwipePointerDown.bind(this);
    this.onSwipePointerMove = this.onSwipePointerMove.bind(this);
    this.onSwipePointerUp = this.onSwipePointerUp.bind(this);
    this.onSwipeTouchMove = this.onSwipeTouchMove.bind(this);
    this.onSwipeClickCapture = this.onSwipeClickCapture.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.onSwipePointerDown);
    this.addEventListener('click', this.onSwipeClickCapture, true);
  }

  disconnectedCallback() {
    this.unbindSwipeMove();
    this.removeEventListener('pointerdown', this.onSwipePointerDown);
    this.removeEventListener('click', this.onSwipeClickCapture, true);
    super.disconnectedCallback();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  isSwipeEnabled() {
    return window.matchMedia(CartDrawerItems.swipeMediaQuery).matches;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onSwipeClickCapture(event) {
    if (event.target.closest('cart-remove-button')) return;
    if (event.target.closest('[data-swipe-view]')) return;

    const swipeAction = event.target.closest('[data-swipe-remove]');
    if (swipeAction && this.isSwipeEnabled()) {
      event.preventDefault();
      event.stopPropagation();
      this.commitSwipeRemove(swipeAction.closest('.cart-item--swipeable'));
      return;
    }

    if (!this.swipeSuppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.swipeSuppressClick = false;
  }

  onSwipePointerDown(event) {
    if (!this.isSwipeEnabled() || event.button) return;
    if (event.target.closest(CartDrawerItems.swipeIgnore)) return;

    const row = event.target.closest('.cart-item--swipeable');
    if (!row || row.classList.contains('is-removing')) return;

    const card = row.querySelector('.cart-item__card');
    if (!card) return;

    this.closeOpenSwipeRows(row);
    this.unbindSwipeMove();

    this.swipeState = {
      row,
      card,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: this.getSwipeOffset(row),
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      locked: false,
      dragging: true,
    };

    row.addEventListener('pointermove', this.onSwipePointerMove, { passive: false });
    row.addEventListener('pointerup', this.onSwipePointerUp);
    row.addEventListener('pointercancel', this.onSwipePointerUp);
    row.addEventListener('touchmove', this.onSwipeTouchMove, { passive: false });
  }

  onSwipeTouchMove(event) {
    if (this.swipeState?.locked) event.preventDefault();
  }

  onSwipePointerMove(event) {
    const state = this.swipeState;
    if (!state?.dragging || event.pointerId !== state.pointerId) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    const now = event.timeStamp;
    const dt = Math.max(now - state.lastTime, 1);
    state.velocity = (event.clientX - state.lastX) / dt;
    state.lastX = event.clientX;
    state.lastTime = now;

    if (!state.locked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        this.resetSwipe(false);
        return;
      }
      state.locked = true;
      state.row.classList.add('is-swiping');
      state.row.style.touchAction = 'none';
      try {
        state.row.setPointerCapture(event.pointerId);
      } catch (error) {}
    }

    event.preventDefault();
    let nextOffset = state.startOffset + dx;
    if (!state.row.querySelector('[data-swipe-remove]') && nextOffset < 0) {
      nextOffset = 0;
    }
    this.setSwipeOffset(state.row, this.rubberband(nextOffset, state.row.offsetWidth));
  }

  onSwipePointerUp(event) {
    const state = this.swipeState;
    if (!state || event.pointerId !== state.pointerId) return;

    const row = state.row;
    const offset = this.getSwipeOffset(row);
    const commitDistance = CartDrawerItems.swipeReveal * 0.72;
    const canRemove = Boolean(row.querySelector('[data-swipe-remove]'));
    const fastLeft = state.locked && state.velocity < -0.45;
    const fastRight = state.locked && state.velocity > 0.45;
    const shouldRemove = canRemove && state.locked && (offset <= -commitDistance || fastLeft);
    const shouldView = state.locked && (offset >= commitDistance || fastRight);
    const shouldRevealRemove = canRemove && state.locked && offset <= -CartDrawerItems.swipeReveal * 0.4;
    const shouldRevealView = state.locked && offset >= CartDrawerItems.swipeReveal * 0.4;

    if (state.locked) {
      this.swipeSuppressClick = true;
      window.setTimeout(() => {
        this.swipeSuppressClick = false;
      }, 350);
    }

    this.unbindSwipeMove();
    row.classList.remove('is-swiping');
    row.style.removeProperty('touch-action');
    this.swipeState = null;

    if (shouldRemove) {
      this.commitSwipeRemove(row);
      return;
    }

    if (shouldView) {
      this.commitSwipeView(row);
      return;
    }

    if (shouldRevealRemove) {
      this.setSwipeOffset(row, -CartDrawerItems.swipeReveal, true);
      return;
    }

    if (shouldRevealView) {
      this.setSwipeOffset(row, CartDrawerItems.swipeReveal, true);
      return;
    }

    this.setSwipeOffset(row, 0, true);
  }

  closeOpenSwipeRows(exceptRow) {
    this.querySelectorAll('.cart-item--swipeable').forEach((row) => {
      if (row === exceptRow || row.classList.contains('is-removing')) return;
      if (this.getSwipeOffset(row) === 0) return;
      this.setSwipeOffset(row, 0, true);
    });
  }

  getSwipeOffset(row) {
    return parseFloat(row.style.getPropertyValue('--swipe-x')) || 0;
  }

  setSwipeOffset(row, offset, animate) {
    const progress = Math.min(Math.abs(offset) / CartDrawerItems.swipeReveal, 1);
    row.style.setProperty('--swipe-x', `${offset}px`);
    row.style.setProperty('--swipe-progress', progress.toFixed(3));
    row.classList.toggle('is-swiping-remove', offset < -1);
    row.classList.toggle('is-swiping-view', offset > 1);
    row.classList.toggle('is-open-remove', offset <= -CartDrawerItems.swipeReveal * 0.9);
    row.classList.toggle('is-open-view', offset >= CartDrawerItems.swipeReveal * 0.9);
    row.classList.toggle('is-commit-ready', progress >= 1);

    if (!animate) return;
    row.classList.remove('is-swiping');
  }

  rubberband(offset, width) {
    const max = Math.min(width * 0.62, CartDrawerItems.swipeReveal + 72);
    const distance = Math.abs(offset);
    if (distance <= max) return offset;
    const limited = max + (distance - max) * 0.22;
    return offset < 0 ? -limited : limited;
  }

  resetSwipe(animate) {
    const state = this.swipeState;
    this.unbindSwipeMove();
    if (state?.row) {
      state.row.classList.remove('is-swiping');
      if (animate) this.setSwipeOffset(state.row, 0, true);
    }
    this.swipeState = null;
  }

  unbindSwipeMove() {
    const state = this.swipeState;
    if (!state?.row) return;
    state.dragging = false;
    state.row.removeEventListener('pointermove', this.onSwipePointerMove);
    state.row.removeEventListener('pointerup', this.onSwipePointerUp);
    state.row.removeEventListener('pointercancel', this.onSwipePointerUp);
    state.row.removeEventListener('touchmove', this.onSwipeTouchMove);
    try {
      if (state.row.hasPointerCapture?.(state.pointerId)) {
        state.row.releasePointerCapture(state.pointerId);
      }
    } catch (error) {}
  }

  commitSwipeRemove(row) {
    if (!row || row.classList.contains('is-removing')) return;

    const removeButton = row.querySelector('cart-remove-button');
    const index = removeButton?.dataset.index || row.dataset.index;
    if (!index) return;

    this.swipeState = null;
    this.swipeSuppressClick = false;
    row.classList.add('is-removing');

    if (!this.prefersReducedMotion()) {
      const height = row.offsetHeight;
      row.style.maxHeight = `${height}px`;
      row.style.setProperty('--swipe-x', `-${Math.round(row.offsetWidth * 1.08)}px`);
      row.style.setProperty('--swipe-progress', '1');
      requestAnimationFrame(() => {
        row.style.maxHeight = '0px';
        row.style.marginBottom = '0px';
      });
    }

    this.updateQuantity(index, 0, { currentTarget: removeButton });
  }

  commitSwipeView(row) {
    if (!row || row.classList.contains('is-removing') || row.classList.contains('is-opening')) return;

    const link = row.querySelector('[data-swipe-view], .cart-item__name');
    const url = link?.getAttribute('href');
    if (!url) return;

    this.swipeState = null;
    this.swipeSuppressClick = false;
    row.classList.add('is-opening');

    if (!this.prefersReducedMotion()) {
      row.style.setProperty('--swipe-x', `${Math.round(row.offsetWidth * 1.08)}px`);
      row.style.setProperty('--swipe-progress', '1');
    }

    window.location.href = url;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
